import type { DateRange } from '@/data/types'
import type * as api from '@/data/api'

export const keys = {
  session: ['session'] as const,
  createParent: ['create-parent'] as const,
  account: (id: string) => ['account', id] as const,
  upline: (id: string) => ['upline', id] as const,
  downline: (query: api.DownlineQuery) => ['downline', query] as const,
  children: (id: string) => ['children', id] as const,
  /**
   * Keyed on the whole query. The overview grew a scope, a sport list and a game
   * list; the old `['overview', from, to]` key would have served a
   * Cricket-filtered snapshot to an unfiltered request and the page would have
   * silently lied.
   */
  overview: (query: api.OverviewQuery) => ['overview', query] as const,
  /** Whole-query key, for the same reason `overview` is: scope and the sport and
   *  game lists all change the numbers. */
  turnover: (query: api.OverviewQuery) => ['turnover', query] as const,
  /** Whole-object key, so every filter and the sort participate automatically. */
  exposure: (query: api.ExposureQuery) => ['exposure', query] as const,
  /**
   * Basis is IN the key. Without it, flipping My share ↔ Total book serves the
   * cached numbers computed under the other basis — the worst possible cache bug
   * on a money screen, because both values are plausible.
   */
  marketBook: (id: string, basis: string) => ['market-book', id, basis] as const,
  /**
   * Nested under the market-book prefix on purpose: one
   * `invalidateQueries({ queryKey: ['market-book'] })` then clears the root and
   * every expanded branch together, so a parent row and its children can never
   * be a poll apart.
   */
  marketBookBranch: (id: string, parentId: string, basis: string) =>
    ['market-book', id, basis, 'branch', parentId] as const,
  pnl: (query: api.PnlQuery) => ['pnl', query] as const,
  memberPnl: (query: api.PnlQuery) => ['member-pnl', query] as const,
  /** Lab Aggregate My P/L (old) — `POST /v1/account/getMasterDashboardAggregator`. */
  myPnl: (query: {
    from: string
    to: string
    subGame: string
    eventId: string
    typeName: string
    page: number
    run: number
  }) => ['my-pnl', query] as const,
  myPnlGrandTotal: (query: {
    from: string
    to: string
    subGame: string
    eventId: string
    typeName: string
    run: number
  }) => ['my-pnl-grand-total', query] as const,
  myPnlEvents: (query: { from: string; to: string; subGame: string; run: number }) =>
    ['my-pnl-events', query] as const,
  myPnlMarkets: (query: {
    from: string
    to: string
    subGame: string
    eventId: string
    eventName: string
    typeName: string
    run: number
  }) => ['my-pnl-markets', query] as const,
  myPnlUsers: (query: {
    from: string
    to: string
    subGame: string
    eventId: string
    eventName: string
    typeName: string
    run: number
  }) => ['my-pnl-users', query] as const,
  /** Lab Aggregate My P/L New — `POST /v1/account/getPLBySubGameForAggregatorBq`. */
  myPnlNew: (query: {
    from: string
    to: string
    subGame: string
    eventId: string
    typeName: string
    page: number
    run: number
  }) => ['my-pnl-new', query] as const,
  myPnlNewEvents: (query: { from: string; to: string; subGame: string; run: number }) =>
    ['my-pnl-new-events', query] as const,
  myPnlNewMarkets: (query: {
    from: string
    to: string
    subGame: string
    eventId: string
    eventName: string
    typeName: string
    run: number
  }) => ['my-pnl-new-markets', query] as const,
  myPnlNewUsers: (query: {
    from: string
    to: string
    subGame: string
    eventId: string
    eventName: string
    typeName: string
    run: number
  }) => ['my-pnl-new-users', query] as const,
  /** Lab PL Statement — Agent-API hierarchy `POST /v1/account/getDownlineAccountsData`. */
  plStatement: (query: api.PnlQuery & {
    q?: string
    tms?: string
    name?: { _id: string; accessLevel?: string }
    game?: string
    subGame?: string
    eventName?: string
    typeName?: string
  }) => ['pl-statement', query] as const,
  /**
   * Lab Net total amount — Agent-API `POST /v1/account/getDownlineAccountsSumOnly`.
   * Keyed on filters only (no page) so pagination does not re-hit the footer API.
   */
  plStatementSumOnly: (query: {
    from: string
    to: string
    q?: string
    tms?: string
    nameId?: string
    game?: string
    subGame?: string
    eventName?: string
    typeName?: string
    memberIds?: string[]
  }) => ['pl-statement-sum-only', query] as const,
  /** Lab Company Master picker — Agent-API hierarchy `POST /v1/member/getAllTm`. */
  allTm: ['all-tm'] as const,
  /** Lab product catalog — Agent-API hierarchy `POST /v1/game/getGames`. */
  games: ['games'] as const,
  /** Lab Sub Game picker — Agent-API hierarchy `POST /v1/account/getSubGames`. */
  subGames: (game: string) => ['sub-games', game] as const,
  /** Lab Event picker — Agent-API hierarchy `POST /v1/account/getEventsForPLL`. */
  eventsForPll: (query: {
    from: string
    to: string
    game?: string
    subGame?: string
    memberIds?: string[]
    tms?: string
    nameId?: string
  }) => ['events-for-pll', query] as const,
  /** Lab display currencies — Agent-API hierarchy `POST /v1/currency/getCurrencies`. */
  currencies: ['currencies'] as const,
  /** Signed-in money view — Agent-API hierarchy `POST /v1/member/getBalance`. */
  memberBalance: ['member-balance'] as const,
  /** Lab P&L row detail — Agent-API hierarchy `POST /v1/account/getOneAccount`. */
  oneAccount: (id: string) => ['one-account', id] as const,
  memberDetail: (id: string, range: DateRange) =>
    ['member-detail', id, range.from, range.to] as const,
  bets: (query: api.BetQuery) => ['bets', query] as const,
  transfers: (query: api.TransferQuery) => ['transfers', query] as const,
  commission: (query: api.PnlQuery) => ['commission', query] as const,
  commissionEligibility: (query: {
    scopeId: string
    memberIds: string[]
    from: string
    to: string
    tab: string
    page: number
    pageSize: number
  }) => ['commission-eligibility', query] as const,
  statement: (id: string, range?: DateRange, currency?: string) =>
    ['statement', id, range?.from, range?.to, currency] as const,
  activity: (limit: number, currency?: string) => ['activity', limit, currency] as const,
  /** Winner CL Transfers — Agent-API `searchCreditLimitLogs`. */
  creditLimitTransfers: (query: { page?: number; pageSize?: number; currency?: string }) =>
    ['credit-limit-transfers', query] as const,
  /** Winner Multiple Device Ids — Player-API `getPlayersWithSameDeviceIds`. */
  multipleDevices: (query: {
    page?: number
    pageSize?: number
    deviceId?: string
    player?: string
  }) => ['multiple-devices', query] as const,
  /** Winner Invalid Bets — Agent-API `getVARBets` / `searchIBLogs`. */
  invalidBets: (query: {
    page?: number
    pageSize?: number
    tab?: string
    members?: string[]
    fromDate?: string
    toDate?: string
  }) => ['invalid-bets', query] as const,
  /** Winner Blocked Device Ids — Agent-API `playersWithBlockedDevices`. */
  blockedDevices: (query: {
    page?: number
    pageSize?: number
    deviceId?: string
    player?: string
  }) => ['blocked-devices', query] as const,
  /** Winner BetfairRates Logs — Agent-API `getBetfairRatesLogs`. */
  betfairRatesLogs: (query: { page?: number; pageSize?: number }) =>
    ['betfair-rates-logs', query] as const,
  /** Winner Blocked IP — Agent-API `getBlockedIps`. */
  blockedIps: (query: {
    page?: number
    pageSize?: number
    ip?: string
    player?: string
  }) => ['blocked-ips', query] as const,
  /** Winner Login From Blocked IP — hierarchy getLoginFromBlockedIps. */
  loginFromBlockedIps: (query: {
    page?: number
    pageSize?: number
    ip?: string
    player?: string
  }) => ['login-from-blocked-ips', query] as const,
  /** Blast radius for stopping one account — read before the confirm, never cached long. */
  stopImpact: (id: string) => ['stop-impact', id] as const,
  search: (query: api.SearchQuery) => ['search', query] as const,
  usernameAvailable: (username: string) => ['username-available', username] as const,
  /** Workstation bet-breakdown member autocomplete (`Member/searchHierarchyNew`). */
  searchHierarchyNew: (query: { filter: string; page?: number }) =>
    ['search-hierarchy-new', query] as const,
  /** People → Hierarchy Search (`Member/searchHierarchyAggregate`). */
  searchHierarchyPage: (query: {
    filter: string[]
    page?: number
  }) => ['search-hierarchy-page', query] as const,
  accessRoles: ['access-roles'] as const,
  staffUsers: (query: { page?: number; keyword?: string }) => ['staff-users', query] as const,
  staffUser: (id: string) => ['staff-user', id] as const,
  /** Player portal MFA adoption (Player-API admin) — not agent Keycloak MFA. */
  playerMfaSummary: ['player-mfa-summary'] as const,
  playerMfaUsers: (query: {
    q?: string
    mfa?: string
    method?: string
    limit?: number
    offset?: number
  }) => ['player-mfa-users', query] as const,
  playerMfaUser: (idOrUsername: string) => ['player-mfa-user', idOrUsername] as const,
  /** P2PKit / PG deposit inspect (Player-API admin). */
  pgDeposits: (query: { username?: string; creditStatus?: string; limit?: number }) =>
    ['pg-deposits', query] as const,
  /** Console Keycloak MFA for another agent (read-only). */
  agentMfaUser: (username: string) => ['agent-mfa-user', username] as const,
  agentMfaBatch: (usernames: string[]) => ['agent-mfa-batch', usernames] as const,
  /** Lab Aggregate Accounts — POST /v1/aggregate-members/search. */
  aggregateAccounts: (username: string, parentId?: string) =>
    ['aggregate-accounts', 'v3', username, parentId ?? ''] as const,
  /**
   * Net Exposure full books (`getBooksForBackend`) — keyed on memberList scope
   * and book basis so My PT vs Total Book never share a cache.
   */
  books: (query: {
    selectedType: string
    eventType: string
    eventname?: string
    roots?: string
    scope?: string
  }) => ['books', query] as const,
  /** Net Exposure inventory (`getBooksForBackendPre`). */
  booksPre: (query: {
    selectedType: string
    eventType: string
    eventname?: string
    roots?: string
    scope?: string
  }) => ['books-pre', query] as const,
  /** Workstation Detail Book — `getTotalBooks1`. */
  workstationTotalBooks: (query: {
    marketId: string
    selectedType: string
    user?: string
    level?: string
  }) => ['workstation-total-books', query] as const,
  /** Family-icon expand — downline books for one parent on a market. */
  workstationDownlineBooks: (query: {
    parentUserId: string
    marketId: string
    selectedType: string
  }) => ['workstation-downline-books', query] as const,
  /** Workstation market-scoped `getBooksForBackend`. */
  workstationBooks: (query: {
    marketId: string
    selectedType: string
    user?: string
    level?: string
  }) => ['workstation-books', query] as const,
  /** Workstation `getMemberNetExposure`. */
  workstationMemberNe: (query: { marketId: string; user?: string }) =>
    ['workstation-member-ne', query] as const,
  /** Workstation bet ticker `getBetDetailsForWorkstation`. */
  workstationBets: (query: {
    marketId: string
    user?: string
    level?: string
    page?: number
    maxRow?: number
    fromDate?: string
    toDate?: string
    userName?: string | string[]
    currencyType?: string
  }) => ['workstation-bets', query] as const,
}
